import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Calendar as CalendarIcon, Save } from "lucide-react";
import RepeatPicker from "./RepeatPicker";

const TYPES = [
    { id: "task", label: "Task" },
    { id: "habit", label: "Habit" },
    { id: "reminder", label: "Reminder" },
    { id: "list", label: "List" },
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Eastern Time today (matches backend's authoritative clock)
function estToday() {
    try {
        return new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    } catch {
        return new Date().toISOString().slice(0, 10);
    }
}

const LEAD_OPTIONS = [
    { value: 0,  label: "None" },
    { value: 5,  label: "5 min" },
    { value: 15, label: "15 min" },
    { value: 30, label: "30 min" },
    { value: 60, label: "1 hour" },
];

// Pull a normalised initial-state bundle from an editing item. Each item type
// has slightly different field names (due_time vs time, starts_on vs date,
// for_date vs date) so this helper centralises the mapping.
function deriveInitialState(editing) {
    if (!editing) {
        return {
            type: null, title: "", priority: "medium",
            time: "09:00", days: [0, 1, 2, 3, 4, 5, 6],
            quantity: "", date: null, leadMinutes: 15,
            recurrence: { type: "none", interval: 1 },
        };
    }
    const { kind, item } = editing;
    const base = {
        type: kind,
        title: item.title || "",
        priority: item.priority || "medium",
        // Empty string when the source item has no time — preserves "no time"
        // intent on save. Pre-fill default 09:00 is only for creation.
        time:
            kind === "task"     ? (item.due_time || "")
          : kind === "reminder" ? (item.time || "")
          : kind === "habit"    ? (item.time || "")
          :                       "",
        days: item.schedule_days || [0, 1, 2, 3, 4, 5, 6],
        quantity: item.quantity || "",
        date:
            kind === "habit" ? (item.starts_on || estToday())
          : kind === "list"  ? (item.for_date || estToday())
          :                    (item.date || estToday()),
        leadMinutes: typeof item.lead_minutes === "number" ? item.lead_minutes : 15,
        recurrence: item.recurrence_type && item.recurrence_type !== "none" ? {
            type: item.recurrence_type,
            interval: item.recurrence_interval || 1,
            unit: item.recurrence_unit || undefined,
            day: item.recurrence_day,
            month: item.recurrence_month,
        } : { type: "none", interval: 1 },
    };
    return base;
}

// Build the PATCH payload sent to the appropriate `update*` API. Only
// includes fields that meaningfully differ from "empty" so we don't blow
// away server-side state with blank values.
function buildEditPatch(kind, state) {
    const patch = { title: state.title.trim() };
    if (kind === "task") {
        patch.priority = state.priority;
        patch.due_time = state.time || null;
        patch.date = state.date;
        const rec = state.recurrence || { type: "none" };
        if (rec.type && rec.type !== "none") {
            patch.recurrence_type = rec.type;
            patch.recurrence_interval = rec.interval || 1;
            if (rec.unit) patch.recurrence_unit = rec.unit;
            if (rec.day !== undefined && rec.day !== null) patch.recurrence_day = rec.day;
            if (rec.month) patch.recurrence_month = rec.month;
        } else {
            patch.recurrence_type = "none";
        }
    } else if (kind === "habit") {
        patch.schedule_days = state.days;
        patch.time = state.time || null;
    } else if (kind === "reminder") {
        patch.time = state.time;
        patch.date = state.date;
        patch.lead_minutes = state.leadMinutes;
    } else if (kind === "list") {
        patch.quantity = state.quantity || "";
    }
    return patch;
}

export default function QuickAddSheet({
    open, onClose, defaultType = "task", defaultDate,
    onSubmit, onSave, editing = null,
}) {
    const isEditing = !!editing;

    // ---- State -------------------------------------------------------------
    const [type, setType] = useState(defaultType);
    const [title, setTitle] = useState("");
    const [priority, setPriority] = useState("medium");
    // Default time so the pill is never visually blank — users can clear or
    // adjust it. 09:00 is a soft "morning" default that reads as a time at a
    // glance. (Empty string when editing an item that had no time, so saving
    // doesn't accidentally ADD a time to an item that didn't want one.)
    const [time, setTime] = useState("09:00");
    const [days, setDays] = useState([0, 1, 2, 3, 4, 5, 6]);
    const [quantity, setQuantity] = useState("");
    const [date, setDate] = useState(defaultDate || estToday());
    const [leadMinutes, setLeadMinutes] = useState(15);
    const [recurrence, setRecurrence] = useState({ type: "none", interval: 1 });

    // ---- Reset on open -----------------------------------------------------
    // Re-derives all fields whenever the sheet opens (creation OR edit). The
    // dependency on `editing?.item?.id` makes sure switching between edited
    // items refreshes the form even if the sheet stays mounted.
    React.useEffect(() => {
        if (!open) return;
        const init = deriveInitialState(editing);
        setType(init.type || defaultType);
        setTitle(init.title);
        setPriority(init.priority);
        setTime(init.time);
        setDays(init.days);
        setQuantity(init.quantity);
        setDate(init.date || defaultDate || estToday());
        setLeadMinutes(init.leadMinutes);
        setRecurrence(init.recurrence);
    }, [open, defaultType, defaultDate, editing]);

    // ---- Submit ------------------------------------------------------------
    const submit = async () => {
        if (!title.trim()) return;

        if (isEditing) {
            const patch = buildEditPatch(editing.kind, {
                title, priority, time, days, quantity, date, leadMinutes, recurrence,
            });
            await onSave?.(editing.kind, editing.item.id, patch);
            onClose();
            return;
        }

        // Creation path — unchanged shape.
        const payload = { type, data: { title: title.trim() } };
        if (type === "task") {
            payload.data.priority = priority;
            if (time) payload.data.due_time = time;
            payload.data.date = date;
            if (recurrence && recurrence.type && recurrence.type !== "none") {
                payload.data.recurrence_type = recurrence.type;
                payload.data.recurrence_interval = recurrence.interval || 1;
                if (recurrence.unit) payload.data.recurrence_unit = recurrence.unit;
                if (recurrence.day !== undefined && recurrence.day !== null) payload.data.recurrence_day = recurrence.day;
                if (recurrence.month) payload.data.recurrence_month = recurrence.month;
            }
        } else if (type === "habit") {
            payload.data.schedule_days = days;
            if (time) payload.data.time = time;
            payload.data.starts_on = date;
        } else if (type === "reminder") {
            if (!time) return;
            payload.data.time = time;
            payload.data.date = date;
            payload.data.lead_minutes = leadMinutes;
        } else if (type === "list") {
            if (quantity) payload.data.quantity = quantity;
            if (date && date !== estToday()) payload.data.for_date = date;
        }
        await onSubmit(payload);
        onClose();
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40 bg-warm-900/30 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    onClick={onClose}
                    data-testid="quickadd-overlay"
                >
                    <motion.div
                        initial={{ y: 60, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 60, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 240, damping: 24 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-cream rounded-t-clay sm:rounded-clay shadow-float p-5 sm:p-6 max-h-[92dvh] overflow-y-auto"
                        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1.25rem)" }}
                        data-testid="quickadd-sheet"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-2xl text-warm-900">
                                {isEditing ? "Edit this one" : "Add something new"}
                            </h3>
                            <button onClick={onClose} className="text-warm-500 hover:text-warm-900 p-1.5 rounded-full hover:bg-surface" data-testid="quickadd-close" aria-label="Close">
                                <X size={18} />
                            </button>
                        </div>

                        {/* Type-tab strip is hidden while editing — you can't turn a task
                            into a habit, that'd be confusing. */}
                        {!isEditing && (
                            <div className="flex gap-1.5 mb-4 bg-surface p-1 rounded-full">
                                {TYPES.map((t) => (
                                    <button
                                        key={t.id}
                                        onClick={() => setType(t.id)}
                                        className={`flex-1 text-xs font-display py-2 rounded-full transition-all ${
                                            type === t.id ? "bg-terracotta text-cream shadow-clay" : "text-warm-800 hover:text-warm-900"
                                        }`}
                                        data-testid={`quickadd-type-${t.id}`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        <input
                            autoFocus
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={
                                type === "list" ? "e.g. Oat milk" : type === "reminder" ? "e.g. Take meds" : type === "habit" ? "e.g. Stretch" : "e.g. Reply to emails"
                            }
                            className="w-full bg-surface rounded-2xl px-4 py-3 font-body text-warm-900 placeholder:text-warm-500 outline-none border-2 border-transparent focus:border-terracotta transition-all"
                            data-testid="quickadd-title"
                            onKeyDown={(e) => e.key === "Enter" && submit()}
                        />

                        {type === "task" && (
                            <div className="mt-3 space-y-3">
                                <div className="flex gap-2">
                                    {["low", "medium", "high"].map((p) => (
                                        <button
                                            key={p}
                                            onClick={() => setPriority(p)}
                                            className={`flex-1 capitalize font-display text-sm py-2 rounded-full transition-all ${
                                                priority === p ? "bg-terracotta text-cream" : "bg-surface text-warm-800 hover:bg-surface-hover"
                                            }`}
                                            data-testid={`quickadd-priority-${p}`}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <DateField value={date} onChange={setDate} testId="quickadd-task-date" allowPast={isEditing} />
                                    <input
                                        type="time"
                                        value={time}
                                        onChange={(e) => setTime(e.target.value)}
                                        className="flex-1 bg-surface rounded-2xl px-4 py-3 font-body text-warm-900 outline-none"
                                        data-testid="quickadd-task-time"
                                        placeholder="Due time"
                                    />
                                </div>
                                <RepeatPicker value={recurrence} onChange={setRecurrence} />
                            </div>
                        )}

                        {type === "habit" && (
                            <div className="mt-3 space-y-3">
                                <div>
                                    <p className="font-body text-xs text-warm-500 uppercase tracking-widest mb-2">Repeat on</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {DAYS.map((d, i) => {
                                            const sel = days.includes(i);
                                            return (
                                                <button
                                                    key={d}
                                                    onClick={() => setDays(sel ? days.filter((x) => x !== i) : [...days, i])}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-display transition-all ${
                                                        sel ? "bg-sage text-cream" : "bg-surface text-warm-500"
                                                    }`}
                                                    data-testid={`quickadd-habit-day-${i}`}
                                                >
                                                    {d}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <DateField value={date} onChange={setDate} testId="quickadd-habit-startson" allowPast={isEditing} />
                                    <input
                                        type="time"
                                        value={time}
                                        onChange={(e) => setTime(e.target.value)}
                                        className="flex-1 bg-surface rounded-2xl px-4 py-3 font-body text-warm-900 outline-none"
                                        data-testid="quickadd-habit-time"
                                        placeholder="Optional time"
                                    />
                                </div>
                                {!isEditing && (
                                    <p className="font-body text-[11px] text-warm-500 -mt-1">
                                        Starts on <strong>{date === estToday() ? "today" : date}</strong>
                                    </p>
                                )}
                            </div>
                        )}

                        {type === "reminder" && (
                            <div className="mt-3 space-y-3">
                                <div className="flex gap-2">
                                    <DateField value={date} onChange={setDate} testId="quickadd-reminder-date" allowPast={isEditing} />
                                    <input
                                        type="time"
                                        value={time}
                                        onChange={(e) => setTime(e.target.value)}
                                        className="flex-1 bg-surface rounded-2xl px-4 py-3 font-body text-warm-900 outline-none"
                                        data-testid="quickadd-reminder-time"
                                        required
                                    />
                                </div>
                                <div>
                                    <p className="font-body text-[11px] uppercase tracking-widest text-warm-500 mb-1.5">
                                        Heads-up before
                                    </p>
                                    <div className="flex flex-wrap gap-1.5" data-testid="quickadd-reminder-lead-group">
                                        {LEAD_OPTIONS.map((opt) => {
                                            const sel = leadMinutes === opt.value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => setLeadMinutes(opt.value)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-display transition-all ${
                                                        sel
                                                            ? "bg-mustard text-warm-900 shadow-clay"
                                                            : "bg-surface text-warm-500 hover:text-warm-900"
                                                    }`}
                                                    data-testid={`quickadd-reminder-lead-${opt.value}`}
                                                    aria-pressed={sel}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="font-body text-[11px] text-warm-500 mt-1.5">
                                        {leadMinutes === 0
                                            ? "Mochi will only ring at the exact time."
                                            : `Mochi will softly nudge you ${leadMinutes} min before.`}
                                    </p>
                                </div>
                            </div>
                        )}

                        {type === "list" && (
                            <div className="mt-3 space-y-3">
                                <input
                                    type="text"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    placeholder="Quantity (optional, e.g. 2 cartons)"
                                    className="w-full bg-surface rounded-2xl px-4 py-3 font-body text-warm-900 placeholder:text-warm-500 outline-none"
                                    data-testid="quickadd-list-quantity"
                                />
                                {!isEditing && (
                                    <div>
                                        <p className="font-body text-[11px] uppercase tracking-widest text-warm-500 mb-1.5">For (optional)</p>
                                        <DateField value={date} onChange={setDate} testId="quickadd-list-for-date" allowPast={isEditing} />
                                    </div>
                                )}
                            </div>
                        )}

                        <button
                            onClick={submit}
                            disabled={!title.trim() || (type === "reminder" && !time)}
                            className="mt-5 w-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-40 text-cream font-display text-lg py-3.5 rounded-full shadow-clay hover:shadow-float hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
                            data-testid="quickadd-submit"
                        >
                            {isEditing
                                ? (<><Save size={18} /> Save changes</>)
                                : (<><Plus size={18} /> Add it</>)
                            }
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function DateField({ value, onChange, testId, allowPast = false }) {
    const today = estToday();
    const isToday = value === today;
    return (
        <label className={`bg-surface rounded-2xl px-3 py-3 flex items-center gap-2 border-2 transition-all cursor-pointer ${
            isToday ? "border-transparent" : "border-terracotta"
        }`}>
            <CalendarIcon size={14} className={isToday ? "text-warm-500" : "text-terracotta"} />
            <input
                type="date"
                value={value}
                // When editing, allow rescheduling to any date — including the past
                // (the item's existing date may already be in the past).
                min={allowPast ? undefined : today}
                onChange={(e) => onChange(e.target.value || today)}
                className="bg-transparent outline-none font-body text-warm-900 w-[120px]"
                data-testid={testId}
            />
        </label>
    );
}
